import { useState, useCallback } from 'react';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

interface UseApiReturn<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  fetch: () => Promise<T | null>;
  update: (data: Partial<T>) => Promise<T | null>;
  reset: () => void;
}

export function useApi<T>(endpoint: string): UseApiReturn<T> {
  const [state, setState] = useState<ApiResponse<T>>({
    data: null,
    error: null,
    loading: false,
  });

  const fetchData = useCallback(async (): Promise<T | null> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(endpoint, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setState({ data, error: null, loading: false });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, error: message, loading: false }));
      return null;
    }
  }, [endpoint]);

  const updateData = useCallback(async (data: Partial<T>): Promise<T | null> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      setState({ data: result, error: null, loading: false });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setState(prev => ({ ...prev, error: message, loading: false }));
      return null;
    }
  }, [endpoint]);

  const reset = useCallback(() => {
    setState({ data: null, error: null, loading: false });
  }, []);

  return {
    ...state,
    fetch: fetchData,
    update: updateData,
    reset,
  };
}

interface UseCrudReturn<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  fetchAll: () => Promise<T[]>;
  create: (data: Partial<T>) => Promise<T | null>;
  update: (id: number | string, data: Partial<T>) => Promise<T | null>;
  remove: (id: number | string) => Promise<boolean>;
}

export function useCrud<T extends { id: number | string }>(endpoint: string): UseCrudReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (): Promise<T[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(endpoint, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setItems(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const create = useCallback(async (data: Partial<T>): Promise<T | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      const item = await response.json();
      setItems(prev => [...prev, item]);
      return item;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const update = useCallback(async (id: number | string, data: Partial<T>): Promise<T | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${endpoint}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      const item = await response.json();
      setItems(prev => prev.map(i => (i.id === id ? item : i)));
      return item;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  const remove = useCallback(async (id: number | string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${endpoint}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      setItems(prev => prev.filter(i => i.id !== id));
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  return { items, loading, error, fetchAll, create, update, remove };
}
